import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mail, Plus, Trash2, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  emailSubscriptionsService,
  type EmailSubscription,
} from "@/services/emailSubscriptions.service";
import { useTranslation } from 'react-i18next';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Abonelik e-postaları: kullanıcılar bildirim almak istedikleri adresleri
 * buradan ekler/çıkarır. Liste her organizasyona özeldir.
 */
export const EmailSubscriptionsCard: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [label, setLabel] = useState("");

  const { data: subscriptions = [], isLoading } = useQuery({
    queryKey: ["email-subscriptions"],
    queryFn: () => emailSubscriptionsService.list(),
  });

  const addMutation = useMutation({
    mutationFn: ({ email, label }: { email: string; label?: string }) =>
      emailSubscriptionsService.add(email, label),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-subscriptions"] });
      setEmail("");
      setLabel("");
      toast.success(t('common:x3.emailAdded', 'E-Mail-Adresse hinzugefügt'));
    },
    onError: (err: any) => {
      const code = err?.response?.data?.code;
      if (code === "already_exists") {
        toast.error(t('common:x3.emailAlreadyExists', 'Diese E-Mail-Adresse ist bereits eingetragen'));
      } else if (code === "invalid_email") {
        toast.error(t('common:x3.invalidEmail', 'Ungültige E-Mail-Adresse'));
      } else {
        toast.error(t('common:x3.emailAddFailed', 'E-Mail-Adresse konnte nicht hinzugefügt werden'));
      }
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => emailSubscriptionsService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-subscriptions"] });
      toast.success(t('common:x3.emailRemoved', 'E-Mail-Adresse entfernt'));
    },
    onError: () => toast.error(t('common:x3.emailRemoveFailed', 'E-Mail-Adresse konnte nicht entfernt werden')),
  });

  const handleAdd = () => {
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      toast.error(t('common:x3.enterValidEmail', 'Bitte geben Sie eine gültige E-Mail-Adresse ein'));
      return;
    }
    addMutation.mutate({ email: value, label: label.trim() || undefined });
  };

  return (
    <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
      <CardHeader className="pb-2">
        <div className="space-y-1">
          <h3 className="text-base font-semibold text-slate-900">
            {t('common:x3.notifyRecipients', 'Benachrichtigungs-Empfänger')}
          </h3>
          <p className="text-sm text-slate-500">
            E-Mail-Adressen, die Benachrichtigungen erhalten sollen. Adressen
            hier hinzufügen oder entfernen.
          </p>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Hinzufügen */}
        <div className="flex flex-col gap-3 sm:flex-row">
          <Input
            type="email"
            placeholder="email@beispiel.de"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAdd();
              }
            }}
            className="sm:flex-1"
          />
          <Input
            type="text"
            placeholder={t('common:x1.labelOptional', 'Bezeichnung (optional)')}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="sm:w-52"
          />
          <Button
            onClick={handleAdd}
            disabled={addMutation.isPending}
            className="bg-[#0F99FD] hover:bg-[#0c84e0] text-white"
          >
            {addMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            <span className="ml-1">{t('common:action.add', 'Hinzufügen')}</span>
          </Button>
        </div>

        {/* Liste */}
        <div className="rounded-xl ring-1 ring-slate-100 divide-y divide-slate-100 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" /> {t('common:x1.loadingDots', 'Wird geladen…')}
            </div>
          ) : subscriptions.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-400">
              {t('common:x3.noEmailsAdded', 'Noch keine E-Mail-Adressen hinzugefügt.')}
            </div>
          ) : (
            subscriptions.map((sub: EmailSubscription) => (
              <div
                key={sub.id}
                className="flex items-center justify-between gap-3 px-4 py-3"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-[#0F99FD]">
                    <Mail className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="truncate font-medium text-gray-900">
                      {sub.email}
                    </div>
                    {sub.label && (
                      <div className="truncate text-xs text-gray-500">
                        {sub.label}
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeMutation.mutate(sub.id)}
                  disabled={removeMutation.isPending}
                  className="shrink-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default EmailSubscriptionsCard;
